import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import './Modal.css';

interface CreateManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (result: any) => void;
}

interface ManagerFormData {
  username: string;
  email: string;
  role: 'regional' | 'district';
  manager_name: string;
  reports_to?: string;
  manages?: string[];
}

const CreateManagerModal: React.FC<CreateManagerModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<ManagerFormData>({
    username: '',
    email: '',
    role: 'district',
    manager_name: '',
    reports_to: '',
    manages: [],
  });

  const [regionalManagers, setRegionalManagers] = useState<string[]>([]);
  const [districtManagers, setDistrictManagers] = useState<string[]>([]);
  const [availableDealers, setAvailableDealers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, formData.role]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (formData.role === 'district') {
        // Fetch regional managers for district manager assignment
        const regionalResponse = await adminAPI.listManagers({ role: 'regional', per_page: 1000 });
        if (regionalResponse.success) {
          const regionals = regionalResponse.managers.map((m: any) => m.manager_name);
          setRegionalManagers(regionals);
        }

        // Fetch dealers for assignment
        const dealersResponse = await adminAPI.listDealers({ per_page: 1000 });
        if (dealersResponse.success) {
          setAvailableDealers(dealersResponse.dealers);
        }
      } else if (formData.role === 'regional') {
        // Fetch district managers for regional manager assignment
        const districtResponse = await adminAPI.listManagers({ role: 'district', per_page: 1000 });
        if (districtResponse.success) {
          const districts = districtResponse.managers.map((m: any) => m.manager_name);
          setDistrictManagers(districts);
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });

    // Auto-generate username from email
    if (name === 'email') {
      const username = value.split('@')[0].toLowerCase();
      setFormData((prev) => ({ ...prev, username }));
    }
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const role = e.target.value as 'regional' | 'district';
    setFormData({
      ...formData,
      role,
      reports_to: '',
      manages: [],
    });
  };

  const handleCheckboxChange = (value: string, checked: boolean) => {
    setFormData((prev) => {
      const manages = checked
        ? [...(prev.manages || []), value]
        : (prev.manages || []).filter((v) => v !== value);
      return { ...prev, manages };
    });
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.manager_name.trim()) {
      newErrors.manager_name = 'Manager name is required';
    } else if (!/^[A-Z]+,[A-Z]+$/.test(formData.manager_name.toUpperCase())) {
      newErrors.manager_name = 'Format must be LASTNAME,FIRSTNAME (e.g., SMITH,JOHN)';
    }

    if (formData.role === 'district' && !formData.reports_to) {
      newErrors.reports_to = 'District managers must report to a regional manager';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      // Split manager_name into last_name and first_name
      const nameParts = formData.manager_name.toUpperCase().split(',');
      if (nameParts.length !== 2) {
        setErrors({ manager_name: 'Invalid format. Use LASTNAME,FIRSTNAME' });
        return;
      }

      const submitData: any = {
        first_name: nameParts[1].trim(),
        last_name: nameParts[0].trim(),
        email: formData.email.toLowerCase(),
        role: formData.role,
      };

      if (formData.role === 'district' && formData.reports_to) {
        submitData.reports_to = formData.reports_to;
      }

      if (formData.manages && formData.manages.length > 0) {
        submitData.manages = formData.manages;
      }

      const response = await adminAPI.createManager(submitData);

      if (response.success) {
        onSuccess(response);
        handleClose();
      } else {
        setErrors({ submit: response.error || 'Failed to create manager' });
      }
    } catch (err: any) {
      console.error('Error creating manager:', err);
      setErrors({
        submit: err.response?.data?.error || err.message || 'Failed to create manager',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      username: '',
      email: '',
      role: 'district',
      manager_name: '',
      reports_to: '',
      manages: [],
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <span className="material-icons">person_add</span>
            Add New Manager
          </h2>
          <button className="modal-close" onClick={handleClose}>
            <span className="material-icons">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="modal-body">
            {errors.submit && (
              <div className="form-error-banner">
                <span className="material-icons">error</span>
                {errors.submit}
              </div>
            )}

            {/* Manager Type */}
            <div className="form-section">
              <h3>Manager Type</h3>

              <div className="form-group">
                <label htmlFor="role">
                  Role <span className="required">*</span>
                </label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleRoleChange}
                >
                  <option value="district">District Manager</option>
                  <option value="regional">Regional Manager</option>
                </select>
              </div>
            </div>

            {/* Manager Information */}
            <div className="form-section">
              <h3>Manager Information</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="manager_name">
                    Manager Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="manager_name"
                    name="manager_name"
                    value={formData.manager_name}
                    onChange={handleInputChange}
                    className={errors.manager_name ? 'error' : ''}
                    placeholder="LASTNAME,FIRSTNAME"
                    style={{ textTransform: 'uppercase' }}
                  />
                  {errors.manager_name && (
                    <span className="field-error">{errors.manager_name}</span>
                  )}
                  <small style={{ color: '#666', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                    Format: LASTNAME,FIRSTNAME (e.g., SMITH,JOHN)
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    Email <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={errors.email ? 'error' : ''}
                    placeholder="manager@ariensco.com"
                  />
                  {errors.email && <span className="field-error">{errors.email}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="username">
                  Username <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className={errors.username ? 'error' : ''}
                  placeholder="Auto-generated from email"
                />
                {errors.username && <span className="field-error">{errors.username}</span>}
                <small style={{ color: '#666', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                  Auto-generated from email (can be edited)
                </small>
              </div>
            </div>

            {/* Hierarchy Assignment */}
            {formData.role === 'district' && (
              <div className="form-section">
                <h3>Reports To</h3>

                <div className="form-group">
                  <label htmlFor="reports_to">
                    Regional Manager <span className="required">*</span>
                  </label>
                  <select
                    id="reports_to"
                    name="reports_to"
                    value={formData.reports_to}
                    onChange={handleInputChange}
                    className={errors.reports_to ? 'error' : ''}
                  >
                    <option value="">Select Regional Manager</option>
                    {regionalManagers.map((rm) => (
                      <option key={rm} value={rm}>
                        {rm}
                      </option>
                    ))}
                  </select>
                  {errors.reports_to && <span className="field-error">{errors.reports_to}</span>}
                </div>
              </div>
            )}

            {/* Assignment Options */}
            {formData.role === 'district' && availableDealers.length > 0 && (
              <div className="form-section">
                <h3>Assign Dealers (Optional)</h3>
                <p style={{ fontSize: '0.875rem', color: '#666', marginBottom: '1rem' }}>
                  Select dealers to assign to this district manager
                </p>

                <div className="checkbox-group" style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '0.5rem' }}>
                  {availableDealers.slice(0, 50).map((dealer) => (
                    <div key={dealer.dealer_id} className="checkbox-item">
                      <input
                        type="checkbox"
                        id={`dealer-${dealer.dealer_id}`}
                        checked={formData.manages?.includes(dealer.dealer_id) || false}
                        onChange={(e) =>
                          handleCheckboxChange(dealer.dealer_id, e.target.checked)
                        }
                      />
                      <label htmlFor={`dealer-${dealer.dealer_id}`}>
                        {dealer.dealer_name} ({dealer.dealer_id})
                      </label>
                    </div>
                  ))}
                </div>
                <small style={{ color: '#666', fontSize: '0.75rem', marginTop: '0.5rem', display: 'block' }}>
                  Showing first 50 dealers. Currently selected: {formData.manages?.length || 0}
                </small>
              </div>
            )}

            {formData.role === 'regional' && districtManagers.length > 0 && (
              <div className="form-section">
                <h3>Assign District Managers (Optional)</h3>
                <p style={{ fontSize: '0.875rem', color: '#666', marginBottom: '1rem' }}>
                  Select district managers to assign to this regional manager
                </p>

                <div className="checkbox-group" style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '0.5rem' }}>
                  {districtManagers.map((dm) => (
                    <div key={dm} className="checkbox-item">
                      <input
                        type="checkbox"
                        id={`district-${dm}`}
                        checked={formData.manages?.includes(dm) || false}
                        onChange={(e) => handleCheckboxChange(dm, e.target.checked)}
                      />
                      <label htmlFor={`district-${dm}`}>{dm}</label>
                    </div>
                  ))}
                </div>
                <small style={{ color: '#666', fontSize: '0.75rem', marginTop: '0.5rem', display: 'block' }}>
                  Currently selected: {formData.manages?.length || 0} district managers
                </small>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting || loading}>
              {submitting ? (
                <>
                  <span className="button-spinner"></span>
                  Creating Manager...
                </>
              ) : (
                <>
                  <span className="material-icons">add</span>
                  Create Manager
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateManagerModal;
